import axios from "axios";
import { getInstance } from "../auth";
import { BrowserClient, Hub, Severity } from "@sentry/browser";
import { getRunAs, getReportingCompany } from "@/helpers/local";

const httpClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 100000
});

const log = new BrowserClient({
  dsn: process.env.VUE_APP_SENTRY_URL,
  environment: process.env.NODE_ENV
});

httpClient.interceptors.request.use(
  config => {
    let instance = getInstance();
    if (!instance.isAuthenticated) {
      instance.loginWithRedirect({ appState: { targetUrl: "/" } });
      return;
    }
    return instance
      .getTokenSilently()
      .then(token => {
        config.headers["Authorization"] = "Bearer " + token;
        if (instance.isSuperAdmin()) {
          let runAs = getRunAs();
          if (runAs) {
            config.headers["X-Run-As-Email"] = getRunAs();
          }
        }
        if (instance.isSuperAdmin() || instance.isCoreCoach()) {
          let reportingCompany = getReportingCompany();
          if (reportingCompany) {
            config.headers["X-Reporting-Customer"] = reportingCompany;
          }
        }
        return Promise.resolve(config);
      })
      .catch(() => {
        instance.loginWithRedirect({ appState: { targetUrl: "/" } });
        return;
      });
  },
  err => {
    return Promise.reject(err);
  }
);

httpClient.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.config) {
      error.message = error.config.url + ": " + error.message;
    }

    const hub = new Hub(log);

    let token = getInstance();

    hub.addBreadcrumb({
      category: "user",
      message: JSON.stringify({
        email: token.userClaims.email,
        role: (token.userClaims["https://api.corecounts.com/roles"] || []).join(
          ","
        )
      }),
      level: Severity.Info
    });

    let config = error.config || {};
    hub.addBreadcrumb({
      category: "xhr",
      message: `${config.method} ${config.url}: ${(error.response || {}).data}`,
      level: Severity.Info
    });

    hub.captureException(error);
    return Promise.reject(error);
  }
);

export default httpClient;
